import { TEN_MINUTES_IN_MS } from '@protector/utils/constants';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { Entitlements, LicensingQueryKey } from './constants.ts';

import type { AppPlansResponse } from './models.ts';
import { getHallWorkspaces, getLicensingStatusByOrg, getLicensingStatusByWorkspace, getProtectorPlans } from './services.ts';
import { getEntitledWorkspaceIds, selectEntitledPlanIds } from './utils.ts';

/*
 * The query client retries nothing by default. A licensing answer decides what a plan allows, so a
 * dropped request would otherwise leave a page gated on nothing until the reader reloads. A caller
 * that would rather see the failure - a test waiting on the error state - passes its own.
 */
const LICENSING_RETRIES = 2;

export function useGetHallWorkspaces(enabled = true) {
  return useQuery({
    queryKey: [LicensingQueryKey.GET_HALL_WORKSPACES],
    queryFn: getHallWorkspaces,
    staleTime: TEN_MINUTES_IN_MS,
    retry: LICENSING_RETRIES,
    enabled
  });
}

export function useHasWorkspaceEntitlement(entitlement: Entitlements, enabled = true) {
  const plans = useGetPlanIdsWithEntitlement(entitlement, enabled);
  const workspaces = useGetHallWorkspaces(enabled);
  const hasError = plans.isError || workspaces.isError;

  return {
    settled: hasError || (plans.isSuccess && workspaces.isSuccess),
    hasEntitlement: !hasError && !!plans.data && !!workspaces.data && getEntitledWorkspaceIds(workspaces.data, plans.data).size > 0
  };
}

export function useGetLicensingStatusByOrg(orgId?: string, enabled = true, retry: number | boolean = LICENSING_RETRIES) {
  return useQuery({
    queryKey: [LicensingQueryKey.GET_LICENSING_STATUS_BY_ORG, orgId],
    queryFn: () => getLicensingStatusByOrg(orgId as string),
    staleTime: TEN_MINUTES_IN_MS,
    retry,
    enabled: !!orgId && enabled
  });
}

export function useGetLicensingStatusByWorkspace(workspaceId?: string, enabled = true, retry: number | boolean = LICENSING_RETRIES) {
  return useQuery({
    queryKey: [LicensingQueryKey.GET_LICENSING_STATUS_BY_WORKSPACE, workspaceId],
    queryFn: () => getLicensingStatusByWorkspace(workspaceId as string),
    staleTime: TEN_MINUTES_IN_MS,
    retry,
    enabled: !!workspaceId && enabled
  });
}

export function useGetPlanIdsWithEntitlement(entitlement: Entitlements, enabled = true) {
  const selectPlanIds = useCallback((response: AppPlansResponse) => selectEntitledPlanIds(response, entitlement), [entitlement]);

  return useQuery({
    queryKey: [LicensingQueryKey.GET_PROTECTOR_PLANS],
    queryFn: getProtectorPlans,
    select: selectPlanIds,
    staleTime: TEN_MINUTES_IN_MS,
    retry: LICENSING_RETRIES,
    enabled
  });
}
